import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSearch, HiX } from 'react-icons/hi';

import { cuttingConfigApi } from '@/api/endpoints/cuttingConfigs';
import { CuttingConfig } from '@/types/cutting';

interface CuttingConfigSearchInputProps {
  value?: CuttingConfig | null;
  onChange: (cuttingConfig: CuttingConfig | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
}

const CuttingConfigSearchInput: React.FC<CuttingConfigSearchInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  className = '',
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CuttingConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasPreloaded, setHasPreloaded] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize search query with selected cutting config name
  useEffect(() => {
    if (value) {
      setSearchQuery(value.name);
    } else {
      setSearchQuery('');
    }
  }, [value]);

  // Search cutting configs when query changes
  useEffect(() => {
    const searchCuttingConfigs = async () => {
      if (searchQuery.length < 1) {
        setSearchResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await cuttingConfigApi.getCuttingConfigs(1, 20, {
          search: searchQuery,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        setSearchResults(response.data);
        setIsOpen(response.data.length > 0);
      } catch (error) {
        console.error('Error searching cutting configs:', error);
        setSearchResults([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCuttingConfigs, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Preload cutting configs on hover - debounced to avoid excessive requests
  const preloadCuttingConfigs = async () => {
    if (hasPreloaded || isLoading) return;

    setIsLoading(true);
    setHasPreloaded(true);
    try {
      const response = await cuttingConfigApi.getCuttingConfigs(1, 20, {
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error preloading cutting configs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside and cleanup hover timeout
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup hover timeout on unmount
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // Clear selection if user is typing
    if (value && newValue !== value.name) {
      onChange(null);
    }
  };

  // Handle cutting config selection
  const handleCuttingConfigSelect = (cuttingConfig: CuttingConfig) => {
    // If no current value (used for adding new cutting configs), clear the search
    if (!value) {
      setSearchQuery('');
    } else {
      setSearchQuery(cuttingConfig.name);
    }
    onChange(cuttingConfig);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle clear selection
  const handleClear = () => {
    setSearchQuery('');
    onChange(null);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle mouse enter - preload data with debouncing
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      preloadCuttingConfigs();
    }, 200); // 200ms debounce
  };

  // Handle mouse leave - clear timeout
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleCuttingConfigSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (searchResults.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          placeholder={placeholder || t('cuttingConfigs.searchPlaceholder')}
          disabled={disabled}
          className={`input-field pl-10 pr-20 ${error ? 'border-red-500' : ''}`}
        />

        {/* Search icon */}
        <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

        {/* Right side icons container */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1">
          {isLoading && (
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
          )}

          {(searchQuery || value) && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <HiX className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {/* Dropdown */}
      {isOpen && searchResults.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {searchResults.map((cuttingConfig, index) => (
            <div
              key={cuttingConfig.id}
              onClick={() => handleCuttingConfigSelect(cuttingConfig)}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex
                  ? 'bg-blue-50 text-blue-900'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{cuttingConfig.name}</div>
                  <div className="text-sm text-gray-500">
                    {cuttingConfig.code}
                  </div>
                  {cuttingConfig.note && (
                    <div className="text-sm text-gray-400 mt-1 truncate">
                      {cuttingConfig.note}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CuttingConfigSearchInput;
